import get from 'lodash/get';
import ValueSet from './ValueSet';
import { ConfiguredOption } from './SurveyItem';
import { integerValueMapper } from '../Components/InputControls/ValueMappers';

export class ChoiceOption {
  display: string;
  value: any;

  constructor(display: string, value: any) {
    this.display = display;
    this.value = value;
  }
}

interface FormToResponseHash {
  [lookupKey: string]: any;
}

/**
 * Wrapper for choice control options
 *
 * @export
 * @class ChoiceOptions
 */
export default class ChoiceOptions {
  static emptyChoiceValue: string = '';

  static initWithValueSet(valueSet: ValueSet): ChoiceOptions {
    if (!valueSet) {
      throw new Error('Value Set is required for initialization');
    }

    const options = valueSet.concepts.map(
      concept => new ChoiceOption(concept.display, concept.fhirCoding),
    );
    return new ChoiceOptions(options, 'valueCoding');
  }

  static initWithConfiguredOption(
    configuredOptions: ConfiguredOption[],
  ): ChoiceOptions {
    if (!configuredOptions) {
      throw new Error('Configured options are required for initialization');
    }

    const options = configuredOptions.map(
      option => new ChoiceOption(option.display, option.originalResourceValue),
    );
    return new ChoiceOptions(options, configuredOptions[0].responseAnswerType);
  }

  _choiceOptions: ChoiceOption[];
  _answerType: string;
  _formToResponseHash: FormToResponseHash = {};
  /**
   * The original type fhir values
   */
  _values: string[] | number[] | fhir.Coding[];

  constructor(selectOptions: ChoiceOption[], answerType: string) {
    this._choiceOptions = selectOptions;
    this._answerType = answerType;
    this._values = this._choiceOptions.map(option => option.value);

    // create hash of values for fast lookup
    this._choiceOptions.forEach(selectOption => {
      const lookupKey = this.responseToFormValue(selectOption.value);
      this._formToResponseHash[lookupKey] = selectOption.value;
    });
  }

  /**
   * Some values must be represented plainly in the form, but as a complex object in the response, this executes the mapping from form -> response
   *
   * @param {*} formValue
   * @returns {*}
   * @memberof ChoiceOptions
   */
  formToResponseValue(formValue: any): any {
    if (this._answerType === 'valueInteger') {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return integerValueMapper.fromInputValue(formValue, null);
    } else if (this._answerType === 'valueCoding') {
      return this._formToResponseHash[formValue];
    } else {
      return formValue;
    }
  }

  /**
   * Some values must be represented plainly in the form, but as a complex object in the response, this executes the mapping from response -> form
   *
   * @param {*} responseValue
   * @returns {*}
   * @memberof ChoiceOptions
   */
  responseToFormValue(responseValue: any): any {
    if (this._answerType === 'valueInteger') {
      return integerValueMapper.toInputValue(responseValue);
    } else if (this._answerType === 'valueCoding') {
      return get(responseValue, 'code', ChoiceOptions.emptyChoiceValue);
    } else {
      return responseValue;
    }
  }

  /**
   * When using fhir search, gets the values of all the options that will directly compare to the fhir search values
   */
  get allFhirSearchValues(): string[] | number[] {
    return this.options.map(_ => {
      if (this._answerType === 'valueCoding') {
        return get(
          _.value as fhir.Coding,
          'code',
          ChoiceOptions.emptyChoiceValue,
        );
      } else {
        return _.value;
      }
    });
  }

  /**
   * Utiltiy function to sort a value set to the originally configured survey list order
   *
   * @param options - value set of all or part of the originally configured options
   */
  orderOptionsToConfiguredOrder(options: any[] = []): any[] {
    const getOriginalIndex = (option: any) => {
      const index = this._choiceOptions.findIndex(choiceOption => {
        const compValue =
          this.selectFieldType === 'coding'
            ? get<fhir.Coding, 'code'>(choiceOption.value, 'code')
            : choiceOption.value;
        return compValue === option;
      });

      if (index === -1) {
        console.error(
          `orderOptionsToConfiguredOrder received the value "${option}" that does not exist in the original value set`,
        );
      }

      return index;
    };

    return [...options].sort(
      (opt1, opt2) => getOriginalIndex(opt1) - getOriginalIndex(opt2),
    );
  }

  get options() {
    return this._choiceOptions || [];
  }

  /**
   * The original type fhir values
   */
  get values() {
    return this._values;
  }

  /**
   * Get the select field type.  Note that not all fhir types are supported
   */
  get selectFieldType(): 'string' | 'integer' | 'coding' | 'date' | 'time' {
    if (this._answerType === 'valueString') {
      return 'string';
    } else if (this._answerType === 'valueInteger') {
      return 'integer';
    } else if (this._answerType === 'valueDate') {
      return 'date';
    } else if (this._answerType === 'valueTime') {
      return 'time';
    } else if (this._answerType === 'valueCoding') {
      return 'coding';
    }

    console.error(
      `Received a answer type that is not supported by the select component.  Type: "${this._answerType}"`,
    );
    return 'string'; // default
  }
}
